import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";

const PAID_ONLY = new Set(["grief180", "sober30", "bundle"]);

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const journal = params.get("journal");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      toast.success("Welcome back.");
      const owned = (result?.user?.programs) || [];
      if (journal && !owned.includes(journal)) {
        if (PAID_ONLY.has(journal)) {
          const { data } = await api.post("/payments/checkout", {
            origin_url: window.location.origin, package_id: journal,
          });
          window.location.href = data.url;
          return;
        }
        if (journal === "chronic365") {
          try { await api.post("/chronic/start-trial"); } catch { /* non-blocking */ }
          navigate("/app/chronic");
          return;
        }
      }
      // Already owns the journal → go to its app route
      const PROGRAM_ROUTE = { journey90: "/app", grief180: "/app/grief", sober30: "/app/sober", chronic365: "/app/chronic" };
      navigate(PROGRAM_ROUTE[journal] || "/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-sand px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-10" data-testid="login-brand">
          <span className="w-2.5 h-2.5 rounded-full bg-moss"></span>
          <span className="font-serif text-xl leading-tight">Rediscover Yourself <span className="text-clay text-sm block">with Angela Gehl</span></span>
        </Link>
        <h1 className="font-serif text-4xl mb-2">Welcome back.</h1>
        <p className="text-muted-foreground mb-8">Continue your journey.</p>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-sage font-bold">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                   data-testid="login-email"
                   className="mt-2 w-full px-5 py-3 rounded-2xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-sage transition-all"
                   placeholder="you@example.com" />
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-sage font-bold">Password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                   data-testid="login-password"
                   className="mt-2 w-full px-5 py-3 rounded-2xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-sage transition-all"
                   placeholder="••••••••" />
          </div>
          <button type="submit" disabled={loading} data-testid="login-submit"
                  className="w-full py-3.5 rounded-full bg-moss text-white hover:bg-mossDark disabled:opacity-60 transition-all">
            {loading ? "Signing you in…" : "Sign in"}
          </button>
        </form>
        <p className="text-center text-sm text-muted-foreground mt-6">
          New here? <Link to={`/register${journal ? `?journal=${journal}` : ""}`} className="text-moss font-medium hover:underline" data-testid="login-to-register">Start your journey</Link>
        </p>
      </div>
    </div>
  );
}
